window.WishlistStore = (() => {
  const state = {
    initialized: false,
    listId: null,
    items: [],
    count: 0,
    productCardsHtml: {},
  };

  let initPromise = null;
  let refreshPromise = null;
  let consentGranted = true;

  function publish(eventName, extra = {}) {
    window.PubSub?.publish?.(eventName, {
      ...extra,
      state: getState(),
    });
  }

  function getState() {
    return {
      ...state,
      items: [...state.items],
    };
  }

  function getItemKey(product) {
    const productId = Number(product?.id);
    const variantId = Number(product?.variantId);

    if (!productId || !variantId) return "";
    return `${productId}_${variantId}`;
  }

  async function ensureProductCardHtml(product) {
    const key = getItemKey(product);
    if (!key) return "";

    if (state.productCardsHtml[key]) {
      return state.productCardsHtml[key];
    }

    const html = await fetchProductCardHtml(product);

    if (html) {
      state.productCardsHtml[key] = html;
    }

    return html;
  }

  function normalizeItems(list) {
     const rawItems = list?.products || list?.items || [];

    return rawItems
      .map((item) => {
        const id = Number(item.id);
        const variantId = Number(item.variantId);

        const savedMeta = window.WishlistMeta?.get?.(id, variantId) || {};
        const savedHandle = savedMeta.handle || "";
        const apiHandle = item.handle || "";

        return {
          id,
          variantId,
          handle: savedHandle || apiHandle,
        };
      })
      .filter((item) => item.id && item.variantId);
  }

  function setItems(list) {
    state.items = normalizeItems(list);
    state.count = state.items.length;

    state.items.forEach((item) => {
      const savedMeta = window.WishlistMeta?.get?.(item.id, item.variantId) || {};

      if (!savedMeta.handle && item.handle) {
        window.WishlistMeta?.save?.({
          id: item.id,
          variantId: item.variantId,
          handle: item.handle,
        });
      }
    });

    const validKeys = new Set(state.items.map(getItemKey).filter(Boolean));

    Object.keys(state.productCardsHtml).forEach((key) => {
      if (!validKeys.has(key)) {
        delete state.productCardsHtml[key];
      }
    });
  }

  function extractHandleFromUrl(url) {
    if (!url) return "";

    try {
      const cleanUrl = String(url).split("?")[0].replace(/\/$/, "");
      const parts = cleanUrl.split("/products/");
      return (parts[1] || cleanUrl.split("/").pop() || "").trim();
    } catch (error) {
      console.error("Failed to extract handle from URL", error);
      return "";
    }
  }

  async function fetchProductCardHtml(product) {
    const productId = Number(product?.id);
    const variantId = Number(product?.variantId);
    const savedMeta = window.WishlistMeta?.get?.(productId, variantId) || {};
    const savedHandle = savedMeta.handle || "";
    const apiHandle = product?.handle || product?.productHandle || "";

   const handleCandidates = [
      savedHandle,
      apiHandle,
    ];

    const uniqueCandidates = [...new Set(handleCandidates.filter(Boolean))];

    for (const candidate of uniqueCandidates) {
      try {
        const response = await fetch(
          `${window.Shopify.routes.root}products/${candidate}?view=wishlist-product-card&variant=${variantId}`
        );

        if (!response.ok) continue;

        const html = await response.text();

        return (
          new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector("body")?.innerHTML || ""
        );
      } catch (error) {
        console.error(`Wishlist product card fetch failed for handle: ${candidate}`, error);
      }
    }

    console.warn("[Wishlist] product handle was not found for item:", product);

    return "";
  }

  function setConsent() {
    consentGranted = true;
  }

  function hasConsent() {
    return consentGranted;
  }

  async function init() {
    if (!hasConsent()) {
      return getState();
    }

    if (state.initialized) {
      return getState();
    }

    if (initPromise) {
      return initPromise;
    }

    initPromise = (async () => {
      try {
        const defaultList = await window.WishlistApi.getDefaultList();

        if (!defaultList?.id) {
          throw new Error("Wishlist default list was not found.");
        }

        state.listId = defaultList.id;
        setItems(defaultList);
        state.initialized = true;

        publish("wishlist:ready");
        return getState();
      } catch (error) {
        publish("wishlist:error", { error });
        throw error;
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  }

  async function refresh(forceSync = false) {
    if (!hasConsent()) {
      return getState();
    }

    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        await init();

        if (!state.initialized || !state.listId) {
          return getState();
        }

        const fullList = await window.WishlistApi.getList(state.listId, {
          forceSync,
        });

        setItems(fullList);

        publish("wishlist:updated");
        return getState();
      } catch (error) {
        console.error("WishlistStore refresh error:", error);
        publish("wishlist:error", { error });
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  function isInWishlist(variantId) {
    if (!hasConsent()) return false;

    return state.items.some((item) => item.variantId === Number(variantId));
  }

  async function add(product) {
    if (!hasConsent()) {
      return getState();
    }

    await init();

    if (!state.initialized || !state.listId) {
      return getState();
    }

    window.WishlistMeta?.save?.(product);

    const updatedList = await window.WishlistApi.addToList(state.listId, product);
    setItems(updatedList);

    const addedItem = state.items.find(
      (item) => item.variantId === Number(product.variantId)
    );

    if (addedItem) {
      ensureProductCardHtml(addedItem);
    }

    publish("wishlist:updated");

    return getState();
  }

  async function remove(product) {
    if (!hasConsent()) {
      return getState();
    }

    await init();

    if (!state.initialized || !state.listId) {
      return getState();
    }

    const updatedList = await window.WishlistApi.removeFromList(state.listId, product);
    setItems(updatedList);
    publish("wishlist:updated");

    return getState();
  }

  return {
    init,
    refresh,
    add,
    remove,
    getState,
    isInWishlist,
    setConsent,
    hasConsent,
    ensureProductCardHtml,
  };
})();